import {
  CountryCode,
  isPossibleNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { z } from 'zod';
import { countries, ShortCode } from '../assets/locales/app/en-countries';

export const fullName = z
  .string()
  .min(1, 'FULL_NAME_REQUIRED')
  .refine((value) => {
    return value.trim().split(/\s+/g).filter(Boolean).length > 1;
  }, 'FULL_NAME_REQUIRED');

export const email = z
  .string()
  .min(1, 'EMAIL_ADDRESS_REQUIRED')
  .regex(
    /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/,
    'EMAIL_ADDRESS_REQUIRED',
  );

export const role = z.enum(['admin', 'user']);

const countryCodeBase = z
  .string({ required_error: 'COUNTRY_CODE_REQUIRED' })
  .min(1, 'COUNTRY_CODE_REQUIRED');

export const countryCode = countryCodeBase
  .startsWith('+', 'COUNTRY_CODE_INVALID')
  .min(2, 'COUNTRY_CODE_INVALID');

export const countryShortCode = z.enum(
  countries.map((c) => c.shortCode) as ShortCode,
);

export const phoneNumber = z
  .string()
  .min(1, 'PHONE_NUMBER_REQUIRED')
  .refine((value) => value.length, {
    message: 'PHONE_NUMBER_REQUIRED',
  });

export const phoneField = (
  {
    countryFieldName,
    phoneFieldName,
  }: {
    countryFieldName: 'country' | 'countryCode';
    phoneFieldName: 'phone' | 'phoneNumber';
  } = {
    countryFieldName: 'country',
    phoneFieldName: 'phone',
  },
) =>
  z
    .object({
      [countryFieldName]: countryShortCode,
      [phoneFieldName]: phoneNumber,
    })
    .transform((value) => {
      try {
        const parsed = parsePhoneNumberWithError(
          value[phoneFieldName],
          value[countryFieldName] as CountryCode,
        );

        return {
          [phoneFieldName]: parsed.nationalNumber.toString(),
          [countryFieldName]: parsed.country ?? value[countryFieldName],
        };
      } catch (err) {
        return value;
      }
    })
    .refine(
      (value) => {
        return isPossibleNumber(
          value[phoneFieldName],
          value[countryFieldName] as CountryCode,
        );
      },
      {
        message: 'PHONE_NUMBER_INVALID',
        path: [phoneFieldName],
      },
    );

export const domain = z
  .string()
  .min(1, 'CONTACT_SALES_FORM.VALIDATIONS.WEBSITE_MISSING')
  .regex(
    new RegExp(
      '^(?:https?:\\/\\/)?(?:www\\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?:[a-z]{2,})$',
    ),
    'CONTACT_SALES_FORM.VALIDATIONS.WEBSITE_INVALID',
  );

export const checkBox = z.enum(['on', 'off']).default('off');
export type ICountryShortCode = z.infer<typeof countryShortCode>;

export const transformDomainPopup = (str: string) => {
  str = str.toLowerCase();
  str = str
    .replace(/\s/g, '')
    .trim()
    .replace('http://', '')
    .replace('https://', '')
    .replace('//', '')
    .split('/')[0]
    .split('?')[0]
    .split('#')[0];
  if (str.substr(0, 4) === 'www.') str = str.replace('www.', '');
  return str;
};

export const domainPopup = z
  .string()
  .transform(transformDomainPopup)
  .refine((value) => {
    return !(
      typeof value !== 'string' ||
      !value ||
      !value.includes('.') ||
      value.includes('..') ||
      value.substr(0, 4) === 'http' ||
      value.substr(-1) === '.' ||
      value.substr(0, 1) === '.' ||
      value.includes(' ') ||
      value.includes('/') ||
      value.substr(0, 4) === 'www.' ||
      value.match(/[A-Z]/) ||
      value.match(/\s/g) ||
      value.replace(/[\-\.]/g, '').match(/[^a-z0-9]/g)
    );
  }, 'DOMAIN_REQUIRED');
